import {
    Home, LayoutDashboard, Box, Package, ShoppingCart, ShoppingBag,
    Truck, Warehouse, Factory, Building2, Users, User,
    FileText, ClipboardList, Receipt, CreditCard,
    DollarSign, BarChart2, PieChart, TrendingUp, Activity,
    Calendar, Clock, Settings, Wrench, Search, Bell,
    ArrowLeftRight, RefreshCw, Download, Database, Globe,
    Map, Star, Shield, Tag, FolderOpen, Archive, Inbox,
    BookOpen, LayoutGrid, List, Table2, Layers, Zap, Monitor,
    // AB#1659 — common page-action verbs so designer-picked action icons render in the tenant.
    Printer, Copy, FileDown, Upload, Trash2, Plus, Check, X, Send, Pencil, Eye, Filter, MapPin,
    // AB#2653 — every icon a SHIPPED module's entity groups/sections declare. Icon() silently
    // renders nothing for an unregistered name, so a group icon missing here is a band with no
    // glyph and no error — which is how Hash/Calculator/CheckCircle shipped icon-less.
    ArrowDownRight, ArrowRightLeft, ArrowUpLeft, Boxes, Building, Calculator, CalendarClock,
    CheckCircle, GitBranch, Hash, Landmark, ListOrdered, Phone, Scale, Settings2, SlidersHorizontal,
    // AB#3089 — the sidebar's sign-out control (an icon button on the user card).
    LogOut, ChevronDown, ChevronRight,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// AB#1659 — the single source-of-truth lucide icon registry shared by the designer's icon picker
// (NavIconPicker) and the tenant runtime (ActionButton). Authoring a nav/action icon picks a name from
// this set; the runtime renders the matching glyph. Keep both in sync by importing from here.
export const LUCIDE_ICON_MAP: Record<string, LucideIcon> = {
    Home, LayoutDashboard, Box, Package, ShoppingCart, ShoppingBag,
    Truck, Warehouse, Factory, Building2, Users, User,
    FileText, ClipboardList, Receipt, CreditCard,
    DollarSign, BarChart2, PieChart, TrendingUp, Activity,
    Calendar, Clock, Settings, Wrench, Search, Bell,
    ArrowLeftRight, RefreshCw, Download, Database, Globe,
    Map, Star, Shield, Tag, FolderOpen, Archive, Inbox,
    BookOpen, LayoutGrid, List, Table2, Layers, Zap, Monitor,
    Printer, Copy, FileDown, Upload, Trash2, Plus, Check, X, Send, Pencil, Eye, Filter, MapPin,
    ArrowDownRight, ArrowRightLeft, ArrowUpLeft, Boxes, Building, Calculator, CalendarClock,
    CheckCircle, GitBranch, Hash, Landmark, ListOrdered, Phone, Scale, Settings2, SlidersHorizontal,
    LogOut, ChevronDown, ChevronRight,
}

export const LUCIDE_ICON_NAMES = Object.keys(LUCIDE_ICON_MAP)

/**
 * AB#1659 — renders a named lucide icon as a glyph. Returns null for an unknown/empty name (so a stale
 * or free-text icon value degrades to no icon rather than leaking the raw name as text).
 */
export function Icon({ name, size = 16, className }: { name?: string | null | undefined; size?: number; className?: string }) {
    if (!name) { return null }
    const Comp = resolveIcon(name)
    if (!Comp) { return null }
    return <Comp size={size} className={className} aria-hidden />
}

/**
 * AB#2722 — the registry is keyed PascalCase (the lucide component names) but every pageAction and nav
 * item in every shipped module authors its icon in KEBAB CASE: `plus`, `refresh-cw`, `arrow-right`. An
 * exact lookup therefore missed them all, and because an unknown name renders nothing rather than
 * failing, the result was buttons with no glyph and no error anywhere — quotes' own Create Sales Order
 * action has shipped icon-less since AB#2636. Exact match still wins first, so a correctly-cased name is
 * unaffected; kebab and snake spellings are then converted and tried once.
 */
export function resolveIcon(name: string): LucideIcon | undefined {
    if (LUCIDE_ICON_MAP[name]) { return LUCIDE_ICON_MAP[name] }
    const pascal = name
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')
    return LUCIDE_ICON_MAP[pascal]
}
